import { useMemo, useState } from 'react';
import { scaleLinear } from 'd3';
import { Chart } from './Chart';
import { compoundColor } from './colors';
import { useDegradation } from '../useDegradation';

function rollupByDriver(rows) {
  const byDriver = new Map();
  for (const s of rows) {
    if (!byDriver.has(s.driver)) byDriver.set(s.driver, []);
    byDriver.get(s.driver).push(s);
  }
  const out = [];
  for (const [driver, list] of byDriver.entries()) {
    list.sort((a, b) => a.slope - b.slope);
    out.push({ driver, best: list[0].slope, stints: list });
  }
  out.sort((a, b) => a.best - b.best);
  return out;
}

const COMPOUND_FILTERS = [
  { id: 'ALL', label: 'All' },
  { id: 'SOFT', label: 'Soft' },
  { id: 'MEDIUM', label: 'Medium' },
  { id: 'HARD', label: 'Hard' },
];

export const PaceDegradation = ({ race, session, selectedDrivers, hideSlowLaps }) => {
  const [compoundFilter, setCompoundFilter] = useState('ALL');

  const { status, slopes } = useDegradation(race, session, selectedDrivers, hideSlowLaps);

  const allRows = useMemo(() => rollupByDriver(slopes || []), [slopes]);

  const rows = useMemo(() => {
    if (compoundFilter === 'ALL') return allRows;
    return allRows
      .map((r) => {
        const matching = r.stints.filter((s) => s.compound === compoundFilter);
        if (matching.length === 0) return null;
        const best = Math.min(...matching.map((s) => s.slope));
        return { driver: r.driver, best, stints: matching };
      })
      .filter(Boolean)
      .sort((a, b) => a.best - b.best);
  }, [allRows, compoundFilter]);

  const renderFilters = () => (
    <div className="flex items-center gap-1 px-5 pt-1 flex-wrap">
      {COMPOUND_FILTERS.map((f) => (
        <button
          key={f.id}
          onClick={() => setCompoundFilter(f.id)}
          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border transition-colors ${
            f.id === compoundFilter
              ? 'bg-f1-red/15 border-f1-red/60 text-f1-text'
              : 'bg-transparent border-f1-border text-f1-muted hover:text-f1-text'
          }`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );

  if (status === 'loading' && allRows.length === 0) {
    return (
      <div className="absolute inset-0 flex flex-col">
        {renderFilters()}
        <div className="relative flex-1 grid place-items-center text-xs text-f1-muted">
          Loading tyre fits…
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="absolute inset-0 flex flex-col">
        {renderFilters()}
        <div className="relative flex-1 grid place-items-center text-xs text-f1-muted px-6 text-center">
          Couldn't load tyre fits.
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="absolute inset-0 flex flex-col">
        {renderFilters()}
        <div className="relative flex-1 grid place-items-center text-xs text-f1-muted px-6 text-center">
          {compoundFilter === 'ALL'
            ? 'No long-run stints with enough laps for a degradation fit.'
            : `No ${compoundFilter.toLowerCase()} stints with enough laps in this session.`}
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col">
      {renderFilters()}
      <div className="relative flex-1">
        <Chart>
          {({ width, height }) => {
            const margin = { top: 4, right: 56, bottom: 18, left: 36 };
            const innerW = Math.max(0, width - margin.left - margin.right);
            const innerH = Math.max(0, height - margin.top - margin.bottom);

            const allSlopes = rows.flatMap((r) => r.stints.map((s) => s.slope));
            const minS = Math.min(0, ...allSlopes);
            const maxS = Math.max(0.05, ...allSlopes);
            const x = scaleLinear().domain([minS, maxS]).range([0, innerW]).nice();
            const visibleRows = rows.slice(0, 14);
            const rowH = innerH / Math.max(visibleRows.length, 1);
            const barH = Math.max(3, Math.min(10, rowH - 2));

            const zeroX = x(0);

            return (
              <svg width={width} height={height}>
                <g transform={`translate(${margin.left},${margin.top})`}>
                  <line x1={zeroX} x2={zeroX} y1={0} y2={innerH} stroke="#3f3f46" strokeDasharray="2,2" />
                  {visibleRows.map((r, i) => {
                    const y = i * rowH + (rowH - barH) / 2;
                    return (
                      <g key={r.driver}>
                        <text x={-6} y={y + barH * 0.85} textAnchor="end" fontSize="9" fill="#9ca3af">
                          {r.driver}
                        </text>
                        {r.stints.map((s, j) => {
                          const xPos = x(s.slope);
                          const left = Math.min(zeroX, xPos);
                          const w = Math.max(1, Math.abs(xPos - zeroX));
                          const stripeY = y + (j * (barH + 1)) / Math.max(r.stints.length, 1);
                          const stripeH = Math.max(2, barH / Math.max(r.stints.length, 1) - 0.5);
                          return (
                            <rect
                              key={j}
                              x={left}
                              y={stripeY}
                              width={w}
                              height={stripeH}
                              fill={compoundColor(s.compound)}
                              opacity={0.85}
                            >
                              <title>
                                {r.driver} · {s.compound.toLowerCase()}
                                {s.stintNumbers.length > 1
                                  ? ` (stints ${s.stintNumbers.join(' + ')})`
                                  : ` (stint ${s.stintNumbers[0]})`}
                                {`\n${s.slope >= 0 ? '+' : ''}${s.slope.toFixed(3)} s/lap (fuel-corrected)`}
                                {`\nraw fit: ${s.rawSlope >= 0 ? '+' : ''}${s.rawSlope.toFixed(3)} s/lap`}
                                {`\n${s.n} laps across ${s.stintNumbers.length} stint${s.stintNumbers.length > 1 ? 's' : ''}`}
                              </title>
                            </rect>
                          );
                        })}
                        <text
                          x={Math.max(zeroX, ...r.stints.map((s) => x(s.slope))) + 4}
                          y={y + barH * 0.85}
                          fontSize="9"
                          fill="#e5e5e5"
                        >
                          {r.best >= 0 ? `+${r.best.toFixed(3)}` : r.best.toFixed(3)}
                          <tspan fill="#6b7280"> s/lap</tspan>
                        </text>
                      </g>
                    );
                  })}
                </g>
                {compoundFilter === 'ALL' && (
                  <g transform={`translate(${margin.left},${height - 4})`}>
                    {[
                      { c: 'SOFT', label: 'Soft' },
                      { c: 'MEDIUM', label: 'Medium' },
                      { c: 'HARD', label: 'Hard' },
                    ].map((l, i) => (
                      <g key={l.c} transform={`translate(${i * 60},-4)`}>
                        <rect width={8} height={8} fill={compoundColor(l.c)} opacity={0.85} />
                        <text x={12} y={7} fontSize="9" fill="#6b7280">{l.label}</text>
                      </g>
                    ))}
                  </g>
                )}
              </svg>
            );
          }}
        </Chart>
      </div>
    </div>
  );
};
